import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  Observable,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  finalize,
  filter,
} from "rxjs";
import { environment } from "src/environments/environment";
import { Client } from "../models/client.model";
import {
  GetClientsQueryParams,
  GetClientsResponseDto,
} from "../dto/clients.dto";

@Injectable({
  providedIn: "root",
})
export class ClientsService {
  private _apiUrl = environment.apiUrl + "/v1/clientes";

  private filtersSubject = new BehaviorSubject<GetClientsQueryParams>({
    "Paginacao.PaginaAtual": 1,
    "Paginacao.QuantidadePorPagina": 10,
    Contato: "",
    Feiras: "",
    Habilitado: "",
    Metro: "",
    Nome: "",
  });

  private loadingSubject = new BehaviorSubject<boolean>(true);

  constructor(private http: HttpClient) {}

  setFilters(filters: Partial<GetClientsQueryParams>): void {
    const currentFilters = this.filtersSubject.value;
    // Preserve pagination settings when updating other filters
    this.filtersSubject.next({ ...currentFilters, ...filters });
  }

  public get loading() {
    return this.loadingSubject.value;
  }

  get filters(): GetClientsQueryParams {
    return this.filtersSubject.value;
  }

  getClientById(id: string): Observable<Client> {
    return this.http.get<Client>(`${this._apiUrl}/${id}`);
  }

  createClient(client: Omit<Client, "id">): Observable<Client> {
    return this.http.post<Client>(this._apiUrl, client);
  }

  editClient(client: Client): Observable<Client> {
    return this.http.put<Client>(`${this._apiUrl}/${client.id}`, client);
  }

  getClients(): Observable<GetClientsResponseDto> {
    return this.filtersSubject.pipe(
      debounceTime(350), // Debounce to avoid unnecessary requests
      distinctUntilChanged(), // Emit only when filters change
      filter((filters) => !filters.Nome || filters.Nome.length >= 2), // Only search after 2 characters
      switchMap((filters) => {
        let params = new HttpParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            params = params.append(key, value.toString());
          }
        });

        this.loadingSubject.next(true); // Set loading state to true
        return this.http
          .get<GetClientsResponseDto>(this._apiUrl, { params })
          .pipe(
            finalize(() => this.loadingSubject.next(false)), // Set loading state to false after request
          );
      }),
    );
  }

  excluirCliente(id: string): Observable<void> {
    return this.http.delete<void>(`${this._apiUrl}/${id}`); 
  }
}
